import React from 'react';
import { cx } from './cx';
import { Button } from './Button';
export { Spinner } from './Spinner';

export const Skeleton = ({ className, style }: { className?: string; style?: React.CSSProperties }) => <div className={cx('animate-pulse rounded-md bg-chip', className)} style={style} aria-hidden="true" />;

interface EmptyStateProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
}

export const EmptyState = ({ title, description, action }: EmptyStateProps) => (
  <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
    <div className="grid h-12 w-12 place-items-center rounded-full bg-brand-50 text-brand-600" aria-hidden="true">∅</div>
    <h3 className="text-base font-semibold text-ink">{title}</h3>
    {description && <p className="max-w-sm text-sm text-muted">{description}</p>}
    {action}
  </div>
);

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
}

/** Shown when a request fails (including backend offline). `onRetry` re-runs the request. */
export const ErrorState = ({ title = 'Something went wrong', message, onRetry }: ErrorStateProps) => (
  <div role="alert" className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
    <div className="grid h-12 w-12 place-items-center rounded-full bg-danger-soft text-danger" aria-hidden="true">!</div>
    <h3 className="text-base font-semibold text-ink">{title}</h3>
    {message && <p className="max-w-sm text-sm text-muted">{message}</p>}
    {onRetry && <Button variant="secondary" size="sm" onClick={onRetry}>Try again</Button>}
  </div>
);

interface InlineAlertProps {
  tone?: 'danger' | 'warn' | 'info';
  children?: React.ReactNode;
}

export const InlineAlert = ({ tone = 'danger', children }: InlineAlertProps) => (
  <div
    role={tone === 'danger' ? 'alert' : 'status'}
    className={cx('rounded-lg px-3 py-2 text-sm', tone === 'danger' ? 'bg-danger-soft text-danger' : tone === 'warn' ? 'bg-warn-soft text-warn' : 'bg-brand-50 text-brand-800')}
  >
    {children}
  </div>
);
